// Live Calls (FaceTime) — WebRTC P2P with polling-based signaling.
// Lives in a React context so any page can ring a friend, accept an
// incoming call, hang up, etc.
//
// Architecture:
//   • Each peer keeps RTCPeerConnections keyed by remote user_id.
//   • SDP offer/answer + ICE candidates go through the backend
//     /signal endpoint; we drain /poll every 1s during signaling.
//   • Media flows directly P2P once ICE has connected — no further
//     server load.
//   • A separate /incoming poll (every 2s) listens for inbound rings
//     while the user is anywhere in the app.
//
// Public API:
//   const {
//     callState,        // {phase, callId, kind, members, joined, incoming}
//     startCall,        // (invitees: string[], opts?) => Promise
//     startBeacon,      // (invitees: string[], context: string) => Promise
//     acceptCall,       // (callId) => Promise
//     declineCall,      // (callId) => Promise
//     hangup,           // () => Promise
//     localStream,      // MediaStream
//     remoteStreams,    // {[user_id]: MediaStream}
//   } = useLiveCalls();
//
// Note: WebRTC requires HTTPS for camera/mic. holywords144.com is
// HTTPS so we're good. Localhost is treated as secure by browsers.
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const Ctx = createContext(null);

const ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

const INITIAL_STATE = { phase: "idle", callId: null, kind: null, members: [], joined: [], incoming: null, context: null, title: null, connectionWarning: null };

export function LiveCallsProvider({ children }) {
  const { user } = useAuth();
  const [callState, setCallState] = useState(INITIAL_STATE);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState({}); // { user_id: MediaStream }
  // Prayer beacons — passive notifications, NOT ringing calls. We keep
  // them in their own queue so the user can call back on their own
  // time (not interrupt whatever they're doing). Each beacon stays in
  // the queue until the user dismisses it OR backend's RING_TTL_SEC
  // expires it server-side.
  const [beacons, setBeacons] = useState([]);
  // Beacons we've already shown to the user since this tab opened — so
  // toasts only fire ONCE per beacon (the poll re-fetches the same
  // active beacons every 2.5s; without this we'd toast every poll).
  const seenBeaconsRef = useRef(new Set());

  // Mutable refs — held outside React state so we don't re-render on
  // every ICE candidate. PCs are keyed by remote user id.
  const pcsRef = useRef({});
  const pollIntervalRef = useRef(null);
  const incomingPollRef = useRef(null);
  // Periodic check of the backend call's `status` so we can tear down
  // locally when the remote peer hangs up (their /hangup flips status
  // to "ended" once all `joined` leave). Without this, the active-call
  // screen would freeze on the friend's last frame.
  const callStatusPollRef = useRef(null);
  const callStateRef = useRef(callState);
  useEffect(() => { callStateRef.current = callState; }, [callState]);

  // Expose a live snapshot of active peer connections so utility
  // components (e.g. the "flip camera" button in CallOverlay) can
  // hot-swap MediaStreamTracks without renegotiating SDP.
  useEffect(() => {
    window.__hwLiveCallsPeers = {
      forEach: (cb) => Object.values(pcsRef.current).forEach(cb),
    };
    return () => { delete window.__hwLiveCallsPeers; };
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────
  const closePc = (uid) => {
    const pc = pcsRef.current[uid];
    if (pc) { try { pc.close(); } catch (_) {} }
    delete pcsRef.current[uid];
    setRemoteStreams((rs) => { const n = { ...rs }; delete n[uid]; return n; });
  };

  const teardown = () => {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    if (callStatusPollRef.current) { clearInterval(callStatusPollRef.current); callStatusPollRef.current = null; }
    Object.keys(pcsRef.current).forEach(closePc);
    if (localStream) { localStream.getTracks().forEach((t) => t.stop()); }
    setLocalStream(null);
    setRemoteStreams({});
    setCallState(INITIAL_STATE);
  };

  // Watch the backend call doc for status === "ended" so we tear down
  // locally when the remote peer hangs up. Cheap (~one HTTP every 3s
  // during an active call only).
  const startCallStatusPoll = (callId) => {
    if (callStatusPollRef.current) clearInterval(callStatusPollRef.current);
    callStatusPollRef.current = setInterval(async () => {
      try {
        const { data: c } = await api.get(`/live-calls/${callId}`);
        if (c.status === "ended") {
          toast("Call ended");
          teardown();
        }
      } catch (e) {
        // 404 = call vanished (deleted / member removed). Treat as ended.
        if (e?.response?.status === 404) teardown();
      }
    }, 3000);
  };

  const ensureLocalStream = async () => {
    if (localStream) return localStream;
    const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    setLocalStream(s);
    return s;
  };

  const buildPc = (callId, remoteUid, stream) => {
    if (pcsRef.current[remoteUid]) return pcsRef.current[remoteUid];
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));
    pc.ontrack = (e) => {
      // Stash the first stream we get from this peer.
      const remote = e.streams[0];
      setRemoteStreams((rs) => ({ ...rs, [remoteUid]: remote }));
      // The instant we hear from a peer, the call is live — flip the
      // initiator's phase off "ringing". Callees already set phase to
      // "live" inside acceptCall().
      setCallState((s) => (s.phase === "ringing" ? { ...s, phase: "live", connectionWarning: null } : s));
    };
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        api.post(`/live-calls/${callId}/signal`, {
          to_user_id: remoteUid,
          type: "ice",
          data: e.candidate.toJSON(),
        }).catch(() => {});
      }
    };
    // ICE health → "Reconnecting…" badge. Browsers move through
    // new → checking → connected → (disconnected/failed). We only
    // surface a warning on disconnected/failed so the badge isn't
    // flapping on every brief network blip.
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      if (st === "disconnected" || st === "failed") {
        setCallState((s) => (s.phase === "live" ? { ...s, connectionWarning: "Reconnecting…" } : s));
      } else if (st === "connected" || st === "completed") {
        setCallState((s) => (s.connectionWarning ? { ...s, connectionWarning: null } : s));
      }
    };
    pcsRef.current[remoteUid] = pc;
    return pc;
  };

  const pollSignals = async (callId) => {
    try {
      const { data: sigs } = await api.get(`/live-calls/${callId}/poll`);
      for (const sig of sigs) {
        await handleSignal(callId, sig);
      }
    } catch (_) { /* silent — keep polling */ }
  };

  const handleSignal = async (callId, sig) => {
    const stream = await ensureLocalStream();
    const pc = buildPc(callId, sig.from_user_id, stream);
    try {
      if (sig.type === "offer") {
        await pc.setRemoteDescription(new RTCSessionDescription(sig.data));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await api.post(`/live-calls/${callId}/signal`, {
          to_user_id: sig.from_user_id,
          type: "answer",
          data: { type: answer.type, sdp: answer.sdp },
        });
      } else if (sig.type === "answer") {
        await pc.setRemoteDescription(new RTCSessionDescription(sig.data));
      } else if (sig.type === "ice") {
        await pc.addIceCandidate(new RTCIceCandidate(sig.data));
      }
    } catch (e) {
      // Don't blow up the whole call on a single bad signal.
      console.warn("[LiveCalls] signal error", e);
    }
  };

  // ── Public actions ────────────────────────────────────────────────
  const startCall = async (invitees, opts = {}) => {
    if (!user) throw new Error("Sign in to call");
    try {
      const stream = await ensureLocalStream();
      const { data: call } = await api.post("/live-calls", {
        kind: invitees.length > 1 ? "group" : "direct",
        invitees,
        title: opts.title || null,
      });
      setCallState({ phase: "ringing", callId: call.id, kind: call.kind, members: call.members, joined: call.members.filter((m) => m === String(user.id)), incoming: null, title: call.title, context: null });
      // We are the offerer. Send an offer to each invitee.
      for (const invitee of invitees) {
        const pc = buildPc(call.id, invitee, stream);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await api.post(`/live-calls/${call.id}/signal`, {
          to_user_id: invitee,
          type: "offer",
          data: { type: offer.type, sdp: offer.sdp },
        });
      }
      pollIntervalRef.current = setInterval(() => pollSignals(call.id), 1000);
      startCallStatusPoll(call.id);
    } catch (e) {
      teardown();
      toast.error(e?.response?.data?.detail || e.message || "Couldn't start call");
      throw e;
    }
  };

  const startBeacon = async (invitees, context) => {
    if (!user) throw new Error("Sign in");
    try {
      await api.post("/live-calls", { kind: "beacon", invitees, context: context || null });
      toast.success("Prayer beacon sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send beacon");
    }
  };

  const acceptCall = async (callId) => {
    try {
      const stream = await ensureLocalStream();
      const { data: call } = await api.post(`/live-calls/${callId}/accept`);
      setCallState({ phase: "live", callId, kind: call.kind, members: call.members, joined: call.members, incoming: null, title: call.title, context: call.context });
      // Prepare PCs for everyone else; they're the offerers so we
      // just wait for their offers to come through the poll loop.
      call.members.filter((m) => m !== String(user.id)).forEach((other) => buildPc(callId, other, stream));
      pollIntervalRef.current = setInterval(() => pollSignals(callId), 1000);
      startCallStatusPoll(callId);
    } catch (e) {
      teardown();
      toast.error(e?.response?.data?.detail || "Couldn't join call");
    }
  };

  const declineCall = async (callId) => {
    try { await api.post(`/live-calls/${callId}/decline`); } catch (_) {}
    setCallState((s) => ({ ...s, incoming: null }));
  };

  const hangup = async () => {
    const id = callStateRef.current.callId;
    if (id) { try { await api.post(`/live-calls/${id}/hangup`); } catch (_) {} }
    teardown();
  };

  // ── Incoming-call listener — runs whenever a user is signed in ────
  useEffect(() => {
    if (!user) {
      if (incomingPollRef.current) { clearInterval(incomingPollRef.current); incomingPollRef.current = null; }
      return;
    }
    const tick = async () => {
      // Don't pop an incoming card while we're already on a live call.
      const phase = callStateRef.current.phase;
      const isBusy = phase === "live" || phase === "ringing";
      try {
        const { data: invites } = await api.get("/live-calls/incoming");
        if (!Array.isArray(invites)) return;
        // Split into rings vs. beacons.
        const rings   = invites.filter((i) => i.kind !== "beacon");
        const beaconsNow = invites.filter((i) => i.kind === "beacon");
        // Rings: show the full-screen accept/decline UI (only when idle).
        if (!isBusy && rings.length) {
          setCallState((s) => ({ ...s, incoming: rings[0] }));
        }
        // Beacons: keep the LIVE list (so a dismissed beacon doesn't
        // come back). One-shot toast the first time we see each.
        setBeacons(beaconsNow);
        for (const b of beaconsNow) {
          if (!seenBeaconsRef.current.has(b.call_id)) {
            seenBeaconsRef.current.add(b.call_id);
            toast(`${b.from_name} needs prayer${b.context ? ` — ${b.context}` : ""}`);
          }
        }
      } catch (_) { /* silent */ }
    };
    incomingPollRef.current = setInterval(tick, 2500);
    // Fire once immediately so the badge isn't blank for the first 2.5s.
    tick();
    return () => { if (incomingPollRef.current) clearInterval(incomingPollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Dismiss a beacon — backend's /decline endpoint already consumes
  // the invite row. We optimistically drop it from local state so the
  // badge updates instantly without waiting for the next poll.
  const dismissBeacon = async (callId) => {
    setBeacons((bs) => bs.filter((b) => b.call_id !== callId));
    try { await api.post(`/live-calls/${callId}/decline`); } catch (_) {}
  };

  const value = useMemo(() => ({
    callState, localStream, remoteStreams, beacons,
    startCall, startBeacon, acceptCall, declineCall, hangup, dismissBeacon,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [callState, localStream, remoteStreams, beacons]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLiveCalls() {
  return useContext(Ctx) || {
    callState: INITIAL_STATE,
    localStream: null,
    remoteStreams: {},
    beacons: [],
    startCall: async () => {},
    startBeacon: async () => {},
    acceptCall: async () => {},
    declineCall: async () => {},
    hangup: async () => {},
    dismissBeacon: async () => {},
  };
}
